/**
 * FR-060…067 · Quản lý quyền người dùng — bám bố cục `roles.html`
 * (danh sách trái · chi tiết phải · ma trận capability 4 nhóm).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, thanhApiError, useLoiApi } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { roleBadge } from '../../lib/format';
import type { CapabilityGroup, ComparisonMatrix, SystemRole } from '../../lib/types';
import { Icon } from '../../components/Icon';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Modal } from '../../components/Modal';
import { Field, SelectField } from '../../components/Field';
import { CapabilityMatrix } from '../../components/CapabilityMatrix';
import { ComparisonTable } from '../../components/ComparisonTable';
import { AsyncSection, EmptyState, SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';

export function RolesPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  const rolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<SystemRole[]>('/roles'),
  });

  const capsQuery = useQuery({
    queryKey: ['roles', 'capabilities'],
    queryFn: () => api.get<CapabilityGroup[]>('/roles/capabilities'),
    staleTime: 10 * 60 * 1000,
  });

  const matrixQuery = useQuery({
    queryKey: ['roles', 'matrix'],
    queryFn: () => api.get<ComparisonMatrix>('/roles/matrix'),
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  const roles = rolesQuery.data ?? [];
  const selected = useMemo(
    () => roles.find((r) => r.id === selectedId) ?? roles[0] ?? null,
    [roles, selectedId],
  );

  useEffect(() => {
    if (selected) {
      setDraft(selected.capabilities);
      setDirty(false);
    }
  }, [selected?.id, selected?.capabilities.join(',')]);

  const [showCreate, setShowCreate] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [editMeta, setEditMeta] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['roles'] });
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const saveCaps = useMutation({
    mutationFn: (payload: { id: string; capabilities: string[] }) =>
      api.patch<SystemRole>(`/roles/${payload.id}`, { capabilities: payload.capabilities }),
    onSuccess: async (role) => {
      await invalidate();
      setDirty(false);
      toast(t('roles.updated', { name: role.name }));
    },
    onError,
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => api.post<SystemRole>(`/roles/${id}/duplicate`),
    onSuccess: async (role) => {
      await invalidate();
      setSelectedId(role.id);
      toast(t('roles.duplicated', { name: role.name }));
    },
    onError,
  });

  const [hoiDatLai, setHoiDatLai] = useState(false);

  const reset = useMutation({
    mutationFn: (id: string) => api.post<SystemRole>(`/roles/${id}/reset`),
    onSuccess: async () => {
      await invalidate();
      toast(t('roles.wasReset'));
    },
    onError,
  });

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 data-od-id="roles-title">{t('roles.title')}</h1>
          <p className="lede">{t('roles.lede')}</p>
        </div>
        <div className="row">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => setShowCreate(true)}
            data-od-id="role-create"
          >
            <Icon name="plus" size={16} />
            {t('roles.add')}
          </button>
        </div>
      </div>

      <AsyncSection
        loading={rolesQuery.isLoading || capsQuery.isLoading}
        error={rolesQuery.error ?? capsQuery.error}
        data={roles.length > 0 && capsQuery.data ? roles : undefined}
        onRetry={() => void rolesQuery.refetch()}
        skeleton={<SkeletonCard rows={6} />}
        empty={<EmptyState title={t('state.empty.title')} />}
      >
        {() => (
          <div className="split-roles">
            <div className="card" data-od-id="role-list-card">
              <div className="card-head" style={{ padding: '10px 12px' }}>
                <span className="eyebrow grow">{t('roles.listTitle')}</span>
                <span className="small muted">{t('roles.count', { count: roles.length })}</span>
              </div>
              <div className="card-body" style={{ padding: 8 }}>
                <div className="picker" data-od-id="role-picker">
                  {roles.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      aria-pressed={selected?.id === role.id}
                      onClick={() => setSelectedId(role.id)}
                    >
                      <span className="badge-lvl">{roleBadge(role.slug)}</span>
                      <span className="who">
                        <b>{role.name}</b>
                        <span>
                          {role.isSystem ? `${t('common.system')} · ` : ''}
                          {role.isDefault ? `${t('common.default')} · ` : ''}
                          {t('roles.userCount', { count: role.userCount })}
                        </span>
                      </span>
                      <span className="n">{role.capabilities.length}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="card-foot">
                <button
                  className="btn btn-sm btn-block"
                  type="button"
                  onClick={() => setShowCreate(true)}
                >
                  {t('roles.addNew')}
                </button>
              </div>
            </div>

            {selected ? (
              <div className="card" data-od-id="role-detail">
                <div className="card-head">
                  <span className="badge-lvl">{roleBadge(selected.slug)}</span>
                  <div className="grow">
                    <h2>{selected.name}</h2>
                    <div className="sub">{selected.description ?? '—'}</div>
                  </div>
                  {selected.isSystem ? (
                    <span className="chip">
                      <span className="dot" />
                      {t('common.system')}
                    </span>
                  ) : null}
                  {selected.isDefault ? (
                    <span className="chip accent">
                      <span className="dot" />
                      {t('common.default')}
                    </span>
                  ) : null}
                  <div className="row">
                    <button
                      className="btn btn-sm"
                      type="button"
                      disabled={selected.isSystem}
                      onClick={() => setEditMeta(true)}
                      data-od-id="role-edit"
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      className="btn btn-sm"
                      type="button"
                      onClick={() => duplicate.mutate(selected.id)}
                      disabled={duplicate.isPending}
                    >
                      {t('common.duplicate')}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      type="button"
                      disabled={selected.isSystem || selected.isDefault}
                      onClick={() => setShowDelete(true)}
                      data-od-id="role-delete"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>

                <div className="card-body" style={{ paddingTop: 12, paddingBottom: 12 }}>
                  {selected.isSystem ? (
                    <div className="scope-note">
                      <Icon name="info" size={17} />
                      <span>{t('roles.systemNote')}</span>
                    </div>
                  ) : selected.isDefault ? (
                    <div className="scope-note accent">
                      <Icon name="info" size={17} />
                      <span>{t('roles.defaultNote')}</span>
                    </div>
                  ) : null}

                  <div className="row mt-14" style={{ gap: 10 }}>
                    <span className="eyebrow">{t('roles.assignedTo')}</span>
                    <span className="member-strip">
                      {selected.userCount > 0 ? (
                        <span className="chip mono">
                          {t('roles.userCount', { count: selected.userCount })}
                        </span>
                      ) : (
                        <span className="small muted">{t('roles.noUser')}</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="card-body" style={{ paddingTop: 0 }}>
                  <CapabilityMatrix
                    groups={capsQuery.data ?? []}
                    selected={draft}
                    readOnly={selected.isSystem}
                    lockedNote={t('roles.systemNote')}
                    onToggle={(id, next) => {
                      setDraft((prev) => (next ? [...prev, id] : prev.filter((cap) => cap !== id)));
                      setDirty(true);
                    }}
                  />
                </div>

                <div className="card-foot">
                  <span className="small muted">{t('roles.footNote')}</span>
                  <span style={{ flex: 1 }} />
                  {selected.resettable && !selected.isSystem ? (
                    <button
                      className="btn btn-sm"
                      type="button"
                      onClick={() => setHoiDatLai(true)}
                      disabled={reset.isPending}
                    >
                      {t('roles.reset')}
                    </button>
                  ) : null}

                  <ConfirmDialog
                    open={hoiDatLai}
                    danger
                    busy={reset.isPending}
                    odId="xac-nhan-dat-lai-vai-tro"
                    title={t('roles.reset')}
                    message={t('roles.resetConfirm', { name: selected.name })}
                    undoNote={t('roles.resetUndo')}
                    confirmLabel={t('roles.reset')}
                    onCancel={() => setHoiDatLai(false)}
                    onConfirm={() => {
                      setHoiDatLai(false);
                      reset.mutate(selected.id);
                    }}
                  />
                  <button
                    className="btn btn-sm btn-primary"
                    type="button"
                    disabled={selected.isSystem || !dirty || saveCaps.isPending}
                    onClick={() => saveCaps.mutate({ id: selected.id, capabilities: draft })}
                  >
                    {saveCaps.isPending ? t('common.processing') : t('roles.saveRole')}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </AsyncSection>

      {matrixQuery.data ? (
        <ComparisonTable
          matrix={matrixQuery.data}
          title={t('roles.matrixTitle')}
          subtitle={t('roles.matrixSub')}
          odId="roles-matrix"
        />
      ) : null}

      {showCreate ? (
        <RoleFormModal
          groups={capsQuery.data ?? []}
          onClose={() => setShowCreate(false)}
          onDone={async (role) => {
            await invalidate();
            setSelectedId(role.id);
            setShowCreate(false);
            toast(t('roles.created', { name: role.name }));
          }}
        />
      ) : null}

      {editMeta && selected ? (
        <RoleFormModal
          groups={capsQuery.data ?? []}
          role={selected}
          onClose={() => setEditMeta(false)}
          onDone={async (role) => {
            await invalidate();
            setEditMeta(false);
            toast(t('roles.updated', { name: role.name }));
          }}
        />
      ) : null}

      {showDelete && selected ? (
        <DeleteRoleModal
          role={selected}
          roles={roles}
          onClose={() => setShowDelete(false)}
          onDone={async () => {
            await invalidate();
            setShowDelete(false);
            setSelectedId(null);
            toast(t('roles.deleted'));
          }}
        />
      ) : null}
    </>
  );
}

function RoleFormModal({
  groups,
  role,
  onClose,
  onDone,
}: {
  groups: CapabilityGroup[];
  role?: SystemRole;
  onClose: () => void;
  onDone: (role: SystemRole) => void | Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [isDefault, setIsDefault] = useState(role?.isDefault ?? false);
  const [caps, setCaps] = useState<string[]>(role?.capabilities ?? []);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const mutation = useMutation({
    mutationFn: () =>
      role
        ? api.patch<SystemRole>(`/roles/${role.id}`, { name, description, isDefault })
        : api.post<SystemRole>('/roles', {
            name,
            description: description || null,
            capabilities: caps,
            isDefault,
          }),
    onSuccess: (data) => void onDone(data),
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      wide={!role}
      title={role ? t('common.edit') : t('roles.add')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('common.save')}
          </button>
        </>
      }
    >
      {error && error.fields.length === 0 ? (
        <div className="form-alert bad" role="alert">
          <Icon name="warn" size={16} />
          <span>{error.message}</span>
        </div>
      ) : null}

      <Field
        label={t('roles.name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        minLength={2}
        autoFocus
        error={error?.fieldMessage('name')}
      />

      <Field
        label={t('roles.description')}
        value={description ?? ''}
        onChange={(e) => setDescription(e.target.value)}
        error={error?.fieldMessage('description')}
      />

      <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
        <input
          className="check"
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        <span className="small">{t('roles.setDefault')}</span>
      </label>

      {!role ? (
        <div>
          <span className="eyebrow">{t('roles.capabilities')}</span>
          {error?.fieldMessage('capabilities') ? (
            <div className="form-alert bad" role="alert" style={{ marginTop: 8 }}>
              <Icon name="warn" size={16} />
              <span>{error.fieldMessage('capabilities')}</span>
            </div>
          ) : null}
          <CapabilityMatrix
            groups={groups}
            selected={caps}
            onToggle={(id, next) =>
              setCaps((prev) => (next ? [...prev, id] : prev.filter((c) => c !== id)))
            }
          />
        </div>
      ) : null}
    </Modal>
  );
}

function DeleteRoleModal({
  role,
  roles,
  onClose,
  onDone,
}: {
  role: SystemRole;
  roles: SystemRole[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const t = useT();
  const others = roles.filter((r) => r.id !== role.id);
  const [fallback, setFallback] = useState(others[0]?.id ?? '');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const mutation = useMutation({
    mutationFn: () =>
      api.del(`/roles/${role.id}`, role.userCount > 0 ? { fallbackRoleId: fallback } : {}),
    onSuccess: () => void onDone(),
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('roles.deleteTitle')}
      subtitle={t('roles.deleteBody', { name: role.name })}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-danger" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('common.delete')}
          </button>
        </>
      }
    >
      {error ? (
        <div className="form-alert bad" role="alert">
          <Icon name="warn" size={16} />
          <span>{error.message}</span>
        </div>
      ) : null}

      {role.userCount > 0 ? (
        <>
          <div className="form-alert warn">
            <Icon name="info" size={16} />
            <span>{t('roles.deleteInUse', { count: role.userCount })}</span>
          </div>
          <SelectField
            label={t('roles.fallbackRole')}
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            required
          >
            {others.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </SelectField>
        </>
      ) : null}
    </Modal>
  );
}
